import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { Fragment } from 'react';
import { Control, useFieldArray, UseFormSetValue } from 'react-hook-form';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { AddOnCharge } from '~hooks/useAddOns/models';
import { Settlement } from '~hooks/useSettlements/models';

import NewSettlementAddOnChargeRow from './NewSettlementAddOnChargeRow';
import SettlementSheetAddOnChargeRow from './SettlementSheetAddOnChargeRow';
import { SettlementSheetColumn, settlementSheetRowSx } from './settlementSheetDisplay';
import {
  canEditSettlementAddOnCharge,
  EMPTY_NEW_ADD_ON_CHARGE,
  SettlementSheetFormValues,
} from './settlementSheetForm';
import SettlementSheetLineItemRow from './SettlementSheetLineItemRow';
import {
  getShownLineItems,
  SettlementSheetCharge,
  SettlementSheetLines,
} from './settlementSheetLines';
import SettlementSheetOrderHeader from './SettlementSheetOrderHeader';
import {
  SettlementSheetTotalAmounts,
  sumShownLineItemTotals,
} from './settlementSheetTotalAmounts';

const columnTitleKeys: Array<[SettlementSheetColumn, string]> = [
  ['date', 'approvals.driver_pay.settlements.pdf.date'],
  ['truck', 'approvals.driver_pay.settlements.pdf.truck_num'],
  ['material', 'approvals.driver_pay.settlements.pdf.material'],
  ['ticket', 'approvals.driver_pay.settlements.pdf.ticket'],
  ['deliveredQuantity', 'approvals.driver_pay.settlements.pdf.qty'],
  ['type', 'approvals.driver_pay.settlements.pdf.type'],
  ['rateType', 'approvals.driver_pay.settlements.pdf.rate_type'],
  ['billingQuantity', 'approvals.driver_pay.settlements.pdf.billing_qty'],
  ['rate', 'approvals.driver_pay.tabs.driver_pay.rate'],
  ['total', 'approvals.driver_pay.settlements.pdf.total'],
];

const sectionHeaderSx = (theme: Theme) => ({
  bgcolor: theme.semantic.surfaceSunken,
  borderBottom: `1px solid ${theme.semantic.borderStrong}`,
  fontWeight: 600,
  px: 2,
  py: 1,
});

interface SettlementSheetLineItemsProps {
  companyId: string;
  control: Control<SettlementSheetFormValues>;
  isAddingAddOnDisabled: boolean;
  isEditing: boolean;
  lines: SettlementSheetLines;
  onRemoveAddOnCharge: (addOnCharge: AddOnCharge) => void;
  onRemoveCharge: (charge: SettlementSheetCharge) => void;
  setValue: UseFormSetValue<SettlementSheetFormValues>;
  settlement: Settlement;
  settlementAddOnCharges: AddOnCharge[];
  totals: SettlementSheetTotalAmounts;
}

export default function SettlementSheetLineItems({
  companyId,
  control,
  isAddingAddOnDisabled,
  isEditing,
  lines,
  onRemoveAddOnCharge,
  onRemoveCharge,
  setValue,
  settlement,
  settlementAddOnCharges,
  totals,
}: SettlementSheetLineItemsProps) {
  const { append, fields, remove } = useFieldArray({ control, name: 'newAddOnCharges' });
  const hasSettlementAddOnCharges =
    settlementAddOnCharges.length > 0 || (isEditing && fields.length > 0);
  const hasLines = lines.orderGroups.length > 0 || hasSettlementAddOnCharges;

  const canRemoveCharge = ({ addOnCharges, lineItems }: SettlementSheetCharge) =>
    isEditing &&
    [...lineItems, ...addOnCharges].every(({ editable }) => editable !== false);

  const getAddOnChargeRowProps = (addOnCharge: AddOnCharge, canRemove: boolean) => ({
    addOnCharge,
    control,
    isEditing,
    onRemove: isEditing && canRemove ? onRemoveAddOnCharge : null,
    totalAmount:
      totals.addOnChargeTotals.get(addOnCharge.id ?? '') ?? addOnCharge.totalAmount,
  });

  return (
    <Box
      sx={(theme) => ({
        bgcolor: 'background.paper',
        border: `1px solid ${theme.semantic.border}`,
        borderRadius: 1,
        overflowX: 'auto',
      })}
    >
      <Box sx={{ minWidth: '100%', width: 'fit-content' }}>
        <Box sx={settlementSheetRowSx}>
          {columnTitleKeys.map(([column, titleKey]) => (
            <Typography
              key={column}
              variant="body2"
              sx={{
                fontWeight: 600,
                gridColumn: column,
                ...(column === 'total' ? { textAlign: 'right' } : {}),
              }}
            >
              {t(titleKey)}
            </Typography>
          ))}
        </Box>

        {!hasLines && (
          <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
            {t('approvals.driver_pay.settlements.lines.empty')}
          </Typography>
        )}

        {lines.orderGroups.map((orderGroup) => (
          <Fragment key={orderGroup.key}>
            <SettlementSheetOrderHeader
              invoice={orderGroup.charges[0]?.invoice ?? orderGroup.invoice}
            />
            {orderGroup.charges.map((charge) => (
              <Box
                key={charge.key}
                data-test-id="settlement-sheet-charge"
                sx={(theme) => ({
                  borderBottom: `1px solid ${theme.semantic.borderStrong}`,
                })}
              >
                {getShownLineItems(charge, isEditing).map(
                  ({ lineItem, rolledUpLineItems }, index) => (
                    <SettlementSheetLineItemRow
                      key={lineItem.id}
                      charge={charge}
                      control={control}
                      isEditing={isEditing}
                      isFirstOfCharge={index === 0}
                      lineItem={lineItem}
                      onRemoveCharge={
                        index === 0 && canRemoveCharge(charge) ? onRemoveCharge : null
                      }
                      rolledUpLineItems={rolledUpLineItems}
                      totalAmount={sumShownLineItemTotals(
                        [lineItem, ...rolledUpLineItems],
                        totals.lineItemTotals,
                      )}
                    />
                  ),
                )}
                {charge.addOnCharges.map((addOnCharge) => (
                  <SettlementSheetAddOnChargeRow
                    key={addOnCharge.id}
                    {...getAddOnChargeRowProps(
                      addOnCharge,
                      addOnCharge.editable !== false,
                    )}
                  />
                ))}
              </Box>
            ))}
            {orderGroup.jobAddOnCharges.map((addOnCharge) => (
              <SettlementSheetAddOnChargeRow
                key={addOnCharge.id}
                {...getAddOnChargeRowProps(addOnCharge, addOnCharge.editable !== false)}
              />
            ))}
          </Fragment>
        ))}

        {hasSettlementAddOnCharges && (
          <>
            <Typography variant="body2" sx={sectionHeaderSx}>
              {t('approvals.driver_pay.settlements.settlement_add_ons')}
            </Typography>
            {settlementAddOnCharges.map((addOnCharge) => (
              <SettlementSheetAddOnChargeRow
                key={addOnCharge.id}
                {...getAddOnChargeRowProps(
                  addOnCharge,
                  canEditSettlementAddOnCharge(settlement, addOnCharge),
                )}
              />
            ))}
            {isEditing &&
              fields.map((field, index) => (
                <NewSettlementAddOnChargeRow
                  key={field.id}
                  companyId={companyId}
                  control={control}
                  index={index}
                  onRemove={remove}
                  setValue={setValue}
                  totalAmount={totals.newAddOnChargeTotals[index] ?? null}
                />
              ))}
          </>
        )}

        {isEditing && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
            }}
          >
            <SmallButton
              color="brandV2Yellow"
              disabled={isAddingAddOnDisabled}
              onClick={() => append(EMPTY_NEW_ADD_ON_CHARGE)}
              startIcon={<Add />}
            >
              {t('approvals.driver_pay.settlements.add_on')}
            </SmallButton>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('approvals.driver_pay.settlements.sheet.edits_notice')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
